
import React, { useState, useEffect, useContext } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons'
import AppContext from '../../../contexts/AppContext'
import { bannerPlaceNameBy } from '../../../services/banners'
import useToastContext from "../../../hooks/useToastContext"
import { numberFormat } from '../../../services/posts'
import { CheckoutForm } from '../../stripe/Stripe'

import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';

import { CheckBoxField } from '../../UI'

const stripePromise = loadStripe(process.env.MIX_STRIPE_PUBLIC_KEY);

const PaymentOptions = ({ bannerId, credits, total, onSuccess, onError }) => {

    const appContext = useContext(AppContext)
    const { pointServices, bannerServices } = appContext

    const [ clientSecret, setClientSecret ] = useState()
    const [ purchasing, setPurchasing ] = useState(false)

    const [ type, setType ] = useState(false)

    const [ creditsFirst, setCreditsFirst ] = useState(false)

    const addToast = useToastContext()

    useEffect(() => {
        if(['stripe'].includes(type)){
            bannerServices.paymentIntent(bannerId, { creditsFirst }).then(res => {
                setClientSecret(res.clientSecret)
            }).catch(err => {
                const message = err?.data?.message ? err.data.message : 'Something went wrong'
                addToast({ type: 'danger', title: 'Error', message: message })
            })
        }
    }, [ type, creditsFirst ])

    const purchase = () => {
        setPurchasing(true)
        return bannerServices.purchase(bannerId, 'credits').then(res => {
            setPurchasing(false)
            addToast({ type: 'success', title: 'Success', message: 'You have successfully pay for your banner ad. Thank you' })
            if(typeof onSuccess == 'function') onSuccess(res)
        }).catch(res => {
            setPurchasing(false)
            const message = res && res.data && res.data.message
            if(message) addToast({title: 'Error', message: message, type: 'danger'})
            onError(res)
        })
    }

    const appearance = {
        theme: 'stripe',
    }

    const options = {
        appearance,
        clientSecret,
    }

    return (
        <>
            <div className="mb-2">
                <small className="text-muted">Total</small>
            </div>
            <div>

                {creditsFirst &&
                    <div>
                        <span>Subtotal: <span className="font-weight-bold">${total}</span></span>
                    </div>
                }

                {credits > 0 &&
                    <div>
                        {type ? (
                            <>
                                {creditsFirst && 'Use credits first'}
                            </>
                        ) : (
                            <CheckBoxField
                                checked={creditsFirst}
                                label="Use credits first"
                                className={''}
                                onClick={() => setCreditsFirst(!creditsFirst)}
                            />
                        )}
                    </div>
                }

                <div>

                    {!creditsFirst &&
                        <span className="font-weight-bold">Total: ${total}</span>
                    }

                    {creditsFirst && total <= credits &&
                        <>
                            <span>Credits: <span className="font-weight-bold">-${total}</span></span><br />
                            <span>Total: <span className="font-weight-bold">${0}</span></span>
                        </>
                    }

                    {creditsFirst && total > credits &&
                        <>
                            <span>Credits: <span className="font-weight-bold">-${credits}</span></span><br />
                            <span>Total: <span className="font-weight-bold">${Math.round((total - credits) * 100) / 100}</span></span>
                        </>
                    }

                </div>

            </div>

            {!type &&
                <div>
                    {!creditsFirst &&
                        <button
                            className="btn btn-success mx-1 my-2"
                            onClick={() => setType('stripe')}
                        >Pay</button>
                    }

                    {creditsFirst && total <= credits &&
                        <button
                            className="btn btn-secondary mx-1 my-2"
                            onClick={purchase}
                        >Pay</button>
                    }

                    {creditsFirst && total > credits &&
                        <button
                            className="btn btn-success mx-1 my-2"
                            onClick={() => setType('stripe')}
                        >Pay</button>
                    }
                </div>
            }

            {['stripe'].includes(type) &&
                <>
                    <button
                        className="btn btn-outline-secondary my-2"
                        onClick={() => setType(null)}
                    >{'< '}Back</button>
                    {clientSecret &&
                        <div>
                            <Elements stripe={stripePromise} options={options}>
                                <CheckoutForm
                                    type="banner"
                                    id={bannerId}
                                    creditsFirst={creditsFirst}
                                />
                            </Elements>
                            <hr />
                            <div className="mt-3 text-center">
                                <small>
                                    <FontAwesomeIcon
                                        icon={faExclamationTriangle}
                                        className="me-2"
                                    />
                                    <b>Warning:</b> Do not place inappropriate ads or anything to do with drugs, sexual/adult content, guns or ammo. These are prohibited ads for our website and we do not allow them. If you load money to your account and create an ad of this type you will not receive a refund for the money you've spent for these types of ad(s). If you create a fraudulent ad you will not get a refund.
                                </small>
                            </div>
                        </div>
                    }
                </>
            }
        </>
    )
}

const PaymentInfo = ({ total, banner, bannerId, onSuccess, onError }) => {

    const appContext = useContext(AppContext)
    const { pointServices } = appContext

    const [ summary, setSummary ] = useState(null)
    const [ loading, setLoading ] = useState(true)

    const credits = summary && summary.points / 100

    const loadSummary = () => {
        pointServices.summary().then(res => {
            setSummary(res.summary)
        }).catch(res => {
            if(res.status == 404) history.push("/page-not-found")
            if(res.status == 500) history.push("/server-error")
        }).finally(() => setLoading(false))
    }

    useEffect(() => {
        loadSummary()
    }, [])

    const canBePaid = [0].includes(banner?.status) && !banner?.has_been_paid
    const processing = [5].includes(banner?.status) && !banner?.has_been_paid
    const hasBeenPaid = banner?.has_been_paid ? true : false

    if(hasBeenPaid) return null

    return (
        <div className="card mb-3">
            <div className="card-body">
                {processing &&
                    <h2 className="m-0">Your payment is processing</h2>
                }
                {canBePaid &&
                    <>
                        <h2 className="mt-0 mb-3">Payment</h2>
                        <div className="row">
                            <div className="col-lg-3 text-center">
                                <div className="mb-2"><small className="text-muted">Purchase amount (credits)</small></div>
                                <h2 className="my-0 h1">{numberFormat(total)}</h2>
                            </div>
                            <div className="col-lg-3 text-center">
                                <div className="mb-2"><small className="text-muted">Your credits available</small></div>
                                <h2 className="my-0 h1">{numberFormat(credits)}</h2>
                                <div><a href="/article/points" className="btn btn-link">Learn how to get free credits</a></div>
                            </div>
                            <div className="col-lg-6 text-center">
                                <PaymentOptions
                                    onSuccess={onSuccess}
                                    onError={onError}
                                    bannerId={bannerId}
                                    credits={credits}
                                    total={total}
                                />
                            </div>
                        </div>
                    </>
                }
            </div>
        </div>
    )
}

const BannerDetails = ({ bannerId, total, setTotal }) => {

    const appContext = useContext(AppContext)
    const { bannerServices } = appContext

    const [ details, setDetails ] = useState(null)
    const [ loading, setLoading ] = useState(true)

    const loadDetails = () => {
        bannerServices.detailsBy(bannerId).then(res => {
            setDetails(res.details)
            setTotal(res.total)
            setLoading(false)
        }).catch(res => {
            if(typeof onUpdated == 'function'){
                setStatus({error: res})
            }
            setLoading(false)
        })
    }

    useEffect(() => {
        loadDetails()
    }, [])

    return (
        <>
            {!loading &&
                <div className="card mb-3">
                    <div className="p-3">
                        <h2 className="my-0">Details</h2>
                    </div>
                    <table className="table my-0">
                        <thead>
                            <tr>
                                <th className="ps-3">Banner type</th>
                                <th>Location, Area</th>
                                <th>Category</th>
                                <th>Duration</th>
                                <th>Price (credits)</th>
                            </tr>
                        </thead>
                        <tbody>
                            {details.map((item, i) => {
                                return (
                                    <tr key={i}>
                                        <td className="ps-3">{bannerPlaceNameBy(item.key)}</td>
                                        <td>{item.location}</td>
                                        <td>{item.category}</td>
                                        <td>{item.duration}</td>
                                        <td>{item.price}</td>
                                    </tr>
                                )
                            })}
                        </tbody>
                        <tfoot>
                            <tr>
                                <th className="ps-3">Total:</th>
                                <th></th>
                                <th></th>
                                <th></th>
                                <th>{total}</th>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            }
        </>
    )

}

const BannerPayment = ({ bannerId, banner, onSuccess, onError }) => {

    const [ showDialog, setShowDialog ] = useState(null)
    const [ dialogOptions, setDialogOptions ] = useState({})
    const [ total, setTotal ] = useState(null)

    const modalDialog = (options) => {
        return new Promise((resolve, reject) => {
            options.onButtonClick = button => {
                if(['cancel', 'close'].includes(button)){
                    setShowDialog(null)
                    reject()
                }
                if(['okay'].includes(button)){
                    setShowDialog(null)
                    resolve()
                }
            }
            options.show = showDialog
            setDialogOptions(options)
            setShowDialog(options.dialog)
        })
    }

    return (
        <>
            <BannerDetails bannerId={bannerId} total={total} setTotal={setTotal} />
            <PaymentInfo total={total} bannerId={bannerId} banner={banner} onSuccess={onSuccess} onError={onError} />
        </>
    )
}

export default BannerPayment

export { }
